import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Container, Chip, Avatar, Button, useTheme, alpha, Paper,
  Divider, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Science as ScienceIcon,
  Medication as PillIcon,
  Flight as PlaneIcon,
  DirectionsCar as CarIcon,
  Storage as ServerIcon,
  Explore as TourIcon,
  Apartment as BuildingIcon,
  AttachMoney as CurrencyIcon,
  LocalGasStation as OilIcon,
  Terrain as MiningIcon,
  Restaurant as FoodIcon,
  WbSunny as SunIcon,
  Favorite as HeartIcon,
  LocalShipping as TruckIcon,
  ArrowUpward as ArrowUpwardIcon,
  Insights as InsightsIcon,
  Launch as LaunchIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import * as industryApi from '../services/industry';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const AllIndustriesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoading(true);
        const data = await industryApi.getAllIndustries({ includeSubIndustries: true });
        setIndustries(data);
      } catch (error) {
        console.error('Error fetching industries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  const getAvatarColor = (industryName: string) => {
    const colors = {
      'Pharmaceutical': '#6366f1',
      'Aerospace': '#06b6d4',
      'Automotive': '#ef4444',
      'Medical Devices': '#10b981',
      'ICT & Data Centers': '#3b82f6',
      'Finance & Banking': '#8b5cf6',
      'Tourism & Hospitality': '#f97316',
      'Real Estate': '#84cc16',
      'Petroleum & Gas': '#78716c',
      'Mining & Minerals': '#a3a3a3',
      'Food & Agriculture': '#f59e0b',
      'Renewable Energy': '#eab308',
      'default': '#6366f1',
    };
    return colors[industryName as keyof typeof colors] || colors.default;
  };

  const getIndustryIcon = (iconName: string) => {
    const iconMap = {
      'PillIcon': <PillIcon />,
      'PlaneIcon': <PlaneIcon />,
      'CarIcon': <CarIcon />,
      'ServerIcon': <ServerIcon />,
      'TourIcon': <TourIcon />,
      'BuildingIcon': <BuildingIcon />,
      'CurrencyIcon': <CurrencyIcon />,
      'OilIcon': <OilIcon />,
      'MiningIcon': <MiningIcon />,
      'FoodIcon': <FoodIcon />,
      'SunIcon': <SunIcon />,
      'HeartIcon': <HeartIcon />,
      'TruckIcon': <TruckIcon />
    };
    return iconMap[iconName as keyof typeof iconMap] || <BusinessIcon />;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Localization': return <BusinessIcon />;
      case 'Service & Infrastructure': return <InventoryIcon />;
      case 'Mature Industries': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Localization': return '#6366f1';
      case 'Service & Infrastructure': return '#06b6d4';
      case 'Mature Industries': return '#10b981';
      default: return '#6366f1';
    }
  };

  const getIndustriesByCategory = (category: string) => {
    return industries.filter(ind => ind.category === category);
  };

  const handleIndustryClick = (industry: any) => {
    // Navigate to individual industry page
    navigate(`/industry/${industry.id}`);
  };

  const renderIndustryCard = (industry: any, index: number) => (
    <Grid item xs={12} sm={6} md={4} key={industry.id}>
      <MotionCard
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.6 }}
        viewport={{ once: true }}
        whileHover={{ y: -8, scale: 1.02 }}
        elevation={2}
        sx={{
          borderRadius: 3,
          height: '100%',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
            borderColor: alpha(theme.palette.primary.main, 0.3)
          }
        }}
      >
        <CardActionArea 
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
          onClick={() => handleIndustryClick(industry)}
        >
          <Box
            sx={{
              height: 6,
              width: '100%',
              background: `linear-gradient(90deg, ${getAvatarColor(industry.name)}, ${theme.palette.secondary.main}, ${alpha(getAvatarColor(industry.name), 0.3)})`
            }}
          />
          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(industry.name),
                  width: 56,
                  height: 56,
                  mr: 2,
                  boxShadow: `0 4px 12px ${alpha(getAvatarColor(industry.name), 0.3)}`
                }}
              >
                {getIndustryIcon(industry.icon)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                    lineHeight: 1.3
                  }}
                >
                  {industry.name}
                </Typography>
                <Chip
                  label={industry.category}
                  size="small"
                  sx={{
                    backgroundColor: alpha(getCategoryColor(industry.category), 0.1),
                    color: getCategoryColor(industry.category),
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ 
                mb: 3, 
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {industry.description}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Key Metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Market Size
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    ${industry.marketSize}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Growth Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main, mr: 0.5 }}>
                      {industry.growthRate}%
                    </Typography>
                    <ArrowUpwardIcon 
                      fontSize="small" 
                      sx={{ color: theme.palette.success.main }} 
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Mini Chart */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                Growth Trend
              </Typography>
              <Box sx={{ height: 80 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={industry.growthData}>
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke={getAvatarColor(industry.name)}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: getAvatarColor(industry.name) }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: 'none',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* Opportunities & Trends - 3 One-liner Bullet Points */}
            {((industry.opportunities && industry.opportunities.length > 0) || (industry.trends && industry.trends.length > 0)) && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  {/* Display up to 3 items total, mixing opportunities and trends */}
                  {[
                    ...(industry.opportunities || []).slice(0, 2).map((item: string) => ({ text: item, type: 'opportunity' })),
                    ...(industry.trends || []).slice(0, 1).map((item: string) => ({ text: item, type: 'trend' }))
                  ].slice(0, 3).map((item: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 1.5,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <DotIcon
                        sx={{
                          fontSize: 8,
                          mt: 1,
                          mr: 1,
                          color: item.type === 'opportunity' ? theme.palette.primary.main : theme.palette.secondary.main,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          lineHeight: 1.6,
                          color: theme.palette.text.secondary,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          fontSize: '0.75rem'
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Bottom Section */}
            <Box sx={{ mt: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Investment Opportunities
                </Typography>
                <Chip
                  label={`${industry.investmentOpportunities} Available`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                    fontWeight: 500
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="outlined"
                color="primary"
                endIcon={<LaunchIcon />}
                sx={{
                  borderRadius: 2.5,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderWidth: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleIndustryClick(industry);
                }}
              >
                Explore Industry
              </Button>
            </Box>
          </CardContent>
        </CardActionArea>
      </MotionCard>
    </Grid>
  );

  const renderSection = (title: string, category: string, subtitle: string) => {
    const sectionIndustries = getIndustriesByCategory(category);
    const categoryColor = getCategoryColor(category);
    
    return (
      <MotionBox
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <SectionHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: categoryColor,
                width: 48,
                height: 48,
                mr: 2,
                boxShadow: `0 4px 12px ${alpha(categoryColor, 0.3)}`
              }}
            >
              {getCategoryIcon(category)}
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}
              >
                {title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>
        </SectionHeader>

        <Grid container spacing={3}>
          {sectionIndustries.map((industry, index) => renderIndustryCard(industry, index))}
        </Grid>
      </MotionBox>
    );
  };

  function handleNavigate(path: string): void {
    navigate(path);
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
            Loading industries...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
      {/* Navigation Breadcrumb */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="text"
          onClick={() => navigate('/home')}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'transparent',
              color: theme.palette.primary.main
            }
          }}
        >
          ← Back to Home
        </Button>
      </Box>

      {/* Hero Section */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(theme.palette.secondary.main, 0.9)})`,
            position: 'relative',
            overflow: 'hidden',
            color: 'white',
            textAlign: 'center',
            border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            All Industries
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 3,
              fontWeight: 500,
              opacity: 0.9
            }}
          >
            Explore Saudi Arabia's diverse industrial landscape and discover investment opportunities across all sectors
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<InsightsIcon />}
              onClick={() => navigate('/analytics')}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.9)
                },
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              View Analytics
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/reports')}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.1)
                },
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>
        </Paper>
      </MotionBox>

      {/* Industry Sections */}
      {renderSection(
        'New Manufacturing Industries',
        'Localization',
        'Emerging sectors driving economic diversification and technological advancement'
      )}

      {renderSection(
        'Service & Infrastructure',
        'Service & Infrastructure',
        'Essential services and infrastructure supporting economic growth'
      )}

      {renderSection(
        'Mature Industries',
        'Mature Industries',
        'Established sectors with proven track records and stable returns'
      )}

      {/* Summary Statistics */}
      <MotionBox
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Paper
          elevation={2}
          sx={{
            mt: 6,
            p: 6,
            borderRadius: 3,
            textAlign: 'center',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Industry Overview
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  {industries.length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Industries
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: 700, color: theme.palette.secondary.main, mb: 1 }}>
                  {industries.reduce((sum, ind) => sum + ind.investmentOpportunities, 0)}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Investment Opportunities
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                  {Math.round(industries.reduce((sum, ind) => sum + ind.growthRate, 0) / industries.length)}%
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Average Growth Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: 700, color: theme.palette.info.main, mb: 1 }}>
                  {industries.reduce((sum, ind) => sum + parseFloat(ind.marketSize.replace('B', '')), 0).toFixed(1)}B
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Market Size
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </MotionBox>
    </Container>
  );
};

export default AllIndustriesPage;